import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Alert,
  LinearProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Block as BlockIcon,
  CheckCircle as ResubscribeIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { marketingApi } from '../../api/marketing';
import type { MarketUnsubscriber } from '../../api/marketing';

const UnsubscribersPage: React.FC = () => {
  const [unsubscribers, setUnsubscribers] = useState<MarketUnsubscriber[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [searchQuery, setSearchQuery] = useState('');
  const [resubscribeDialog, setResubscribeDialog] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState('');

  useEffect(() => {
    fetchUnsubscribers();
  }, [page, rowsPerPage, searchQuery]);

  const fetchUnsubscribers = async () => {
    try {
      setLoading(true);
      const response = await marketingApi.getUnsubscribers({
        page: page + 1,
        search: searchQuery || undefined,
        is_active: true,
      });
      setUnsubscribers(response.results);
      setTotalCount(response.count);
    } catch (error) {
      console.error('Failed to fetch unsubscribers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResubscribe = async () => {
    try {
      await marketingApi.resubscribe(selectedEmail);
      setResubscribeDialog(false);
      setSelectedEmail('');
      await fetchUnsubscribers();
    } catch (error) {
      console.error('Failed to resubscribe:', error);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getMethodColor = (method: string): 'default' | 'primary' | 'secondary' | 'warning' => {
    const colors: Record<string, any> = {
      link: 'primary',
      manual: 'secondary',
      admin: 'warning',
      api: 'default',
    };
    return colors[method] || 'default';
  };

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" component="h1">
            Unsubscribers
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Users who have opted out of all marketing emails
          </Typography>
        </Box>
        <IconButton onClick={fetchUnsubscribers} color="primary">
          <RefreshIcon />
        </IconButton>
      </Box>

      <Alert severity="warning" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Global Unsubscribe Policy:</strong> When a user unsubscribes from any campaign,
          they are automatically blocked from ALL future marketing emails. This is a compliance
          requirement to respect user preferences.
        </Typography>
      </Alert>

      {/* Search */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search by email..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ maxWidth: 500 }}
        />
      </Paper>

      {/* Statistics */}
      <Box display="flex" gap={2} mb={3}>
        <Paper sx={{ p: 2, flex: 1 }}>
          <Box display="flex" alignItems="center">
            <BlockIcon color="error" sx={{ mr: 1 }} />
            <Box>
              <Typography variant="h6">{totalCount}</Typography>
              <Typography variant="body2" color="textSecondary">
                Total Unsubscribers
              </Typography>
            </Box>
          </Box>
        </Paper>
        <Paper sx={{ p: 2, flex: 1 }}>
          <Box display="flex" alignItems="center">
            <InfoIcon color="info" sx={{ mr: 1 }} />
            <Box>
              <Typography variant="body2" color="textSecondary">
                Unsubscribe Rate
              </Typography>
              <Typography variant="body2">
                Calculated per campaign
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>

      {/* Unsubscribers Table */}
      <Paper>
        {loading && <LinearProgress />}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>Campaign</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Method</TableCell>
                <TableCell>Unsubscribed Date</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {unsubscribers.map((unsubscriber) => (
                <TableRow key={unsubscriber.id}>
                  <TableCell>
                    <Typography variant="body2">{unsubscriber.email}</Typography>
                  </TableCell>
                  <TableCell>
                    {unsubscriber.campaign_name || '-'}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ maxWidth: 200 }}>
                      {unsubscriber.reason || 'User clicked unsubscribe'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={unsubscriber.method || 'link'}
                      size="small"
                      color={getMethodColor(unsubscriber.method)}
                    />
                  </TableCell>
                  <TableCell>
                    {new Date(unsubscriber.unsubscribed_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell align="center">
                    {unsubscriber.is_active ? (
                      <Chip label="Active" color="error" size="small" />
                    ) : (
                      <Chip label="Resubscribed" color="success" size="small" />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {unsubscriber.is_active && (
                      <Tooltip title="Resubscribe this email">
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() => {
                            setSelectedEmail(unsubscriber.email);
                            setResubscribeDialog(true);
                          }}
                        >
                          <ResubscribeIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {unsubscribers.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="textSecondary">
                      No unsubscribers found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[25, 50, 100, 200]}
        />
      </Paper>

      {/* Resubscribe Confirmation Dialog */}
      <Dialog
        open={resubscribeDialog}
        onClose={() => setResubscribeDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Resubscribe User</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Are you sure you want to resubscribe <strong>{selectedEmail}</strong>?
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              This will allow them to receive marketing emails again. Make sure you have
              their explicit consent to resubscribe them.
            </Typography>
          </Alert>
          <Alert severity="info">
            <Typography variant="body2">
              <strong>Compliance Note:</strong> Only resubscribe users who have explicitly
              requested to receive emails again. Unsolicited emails may violate anti-spam laws.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResubscribeDialog(false)}>
            Cancel
          </Button>
          <Button onClick={handleResubscribe} variant="contained" color="success">
            Confirm Resubscribe
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UnsubscribersPage;